import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import FichaActivoDialog from '../components/FichaActivoDialog';
import { ESTADO_COLORS, TIPO_COLORS, TIPO_LABELS, ITEM_COLORS, Badge } from '../utils/mantenimientoBadges';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ESTADOS = ['Pendiente', 'En Proceso', 'Finalizada', 'Cancelada'];
const TIPOS = ['Preventivo', 'Correctivo', 'Inspeccion'];

const ESTADO_OPTIONS = [
    { label: 'Todos', value: '' },
    ...ESTADOS.map(e => ({ label: e, value: e })),
];

const initialFormData = {
    mantenimientoId: 0,
    itemMantenimientoId: null,
    fecha: null,
    tipo: 'Correctivo',
    descripcion: '',
    responsable: '',
    costo: null,
    estado: 'Pendiente',
};

// ── Componente ────────────────────────────────────────────────────────────────

const MantenimientoAdmin = () => {
    const toast      = useRef(null);
    const HOST_API   = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [mantenimientos,   setMantenimientos]   = useState([]);
    const [items,            setItems]            = useState([]);
    const [loading,          setLoading]          = useState(false);
    const [globalFilter,     setGlobalFilter]     = useState('');
    const [filtroEstado,     setFiltroEstado]     = useState('');
    const [filtroFecha,      setFiltroFecha]      = useState(null);
    const [filtroItem,       setFiltroItem]       = useState(null);
    const [vistaCalendario,  setVistaCalendario]  = useState(false);
    const [formData,         setFormData]         = useState(initialFormData);
    const [visible,          setVisible]          = useState(false);
    const [fichaVisible,     setFichaVisible]     = useState(false);
    const [fichaItemId,      setFichaItemId]      = useState(null);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const eventosCalendario = useMemo(() => mantenimientos.map(m => {
        const fecha = m.fecha?.substring(0, 10) ?? '';
        const color = (ESTADO_COLORS[m.estado] || ESTADO_COLORS['Pendiente']).dot;
        return {
            id:              String(m.mantenimientoId),
            title:           m.itemNombre,
            start:           fecha,
            allDay:          true,
            backgroundColor: color,
            borderColor:     color,
            extendedProps:   { mantenimiento: m },
        };
    }), [mantenimientos]);

    // ── Fetch ──
    const fetchMantenimientos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/mantenimiento/GetMantenimientos?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setMantenimientos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los mantenimientos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    const fetchItems = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(
                `${HOST_API}api/itemsMantenimiento/GetItems?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setItems(data);
        } catch { /* silencioso */ }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchMantenimientos(); fetchItems(); }, [fetchMantenimientos, fetchItems]);

    // ── Alta / edición ──
    const handleNuevo = () => { setFormData(initialFormData); setVisible(true); };

    const handleEdit = (row) => {
        setFormData({
            mantenimientoId:     row.mantenimientoId,
            itemMantenimientoId: row.itemMantenimientoId,
            fecha:                row.fecha ? new Date(row.fecha) : null,
            tipo:                 row.tipo || 'Correctivo',
            descripcion:          row.descripcion || '',
            responsable:          row.responsable || '',
            costo:                row.costo ?? null,
            estado:               row.estado || 'Pendiente',
        });
        setVisible(true);
    };

    // Abre la ficha del activo (historia clínica + diagnóstico de IA) al clickear
    // el nombre del ítem en la grilla.
    const handleVerFicha = (itemMantenimientoId) => {
        setFichaItemId(itemMantenimientoId);
        setFichaVisible(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.itemMantenimientoId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná un ítem', life: 3000 });
            return;
        }
        if (!formData.fecha) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'La fecha es obligatoria', life: 3000 });
            return;
        }
        if (!formData.descripcion.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'La descripción es obligatoria', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/mantenimiento/SaveMantenimiento`, {
                ...formData,
                fecha: formData.fecha.toISOString().substring(0, 10),
                barrioId: barrio.id,
                administracionId,
                usuarioId,
            });
            setVisible(false);
            fetchMantenimientos();
            toast.current.show({
                severity: 'success',
                summary: formData.mantenimientoId ? 'Actualizado' : 'Creado',
                detail: `Mantenimiento ${formData.mantenimientoId ? 'actualizado' : 'creado'} correctamente`,
                life: 2500,
            });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el mantenimiento', life: 3000 });
        }
    };

    // ── Eliminar ──
    const handleDelete = (m) => {
        confirmDialog({
            message: `¿Eliminar la tarea de mantenimiento de "${m.itemNombre}" del ${fmtFecha(m.fecha)}?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/mantenimiento/DeleteMantenimiento?mantenimientoId=${m.mantenimientoId}&administracionId=${administracionId}`);
                    fetchMantenimientos();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Mantenimiento eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    // ── Filtros aplicados localmente ──
    const mantenimientosFiltrados = mantenimientos.filter(m => {
        if (filtroEstado && m.estado !== filtroEstado) return false;
        if (filtroItem  && m.itemMantenimientoId !== filtroItem) return false;
        if (filtroFecha) {
            const df = filtroFecha.toISOString().substring(0, 10);
            if (new Date(m.fecha).toISOString().substring(0, 10) !== df) return false;
        }
        if (globalFilter) {
            const q = globalFilter.toLowerCase();
            return (
                m.itemNombre?.toLowerCase().includes(q)   ||
                m.descripcion?.toLowerCase().includes(q)  ||
                m.responsable?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // ── Templates de columnas ──
    const estadoTemplate = (m) => (
        <Badge label={m.estado} colors={ESTADO_COLORS[m.estado] || ESTADO_COLORS['Pendiente']} />
    );

    const itemTemplate = (m) => (
        <Badge
            label={m.itemNombre}
            colors={ITEM_COLORS}
            onClick={() => handleVerFicha(m.itemMantenimientoId)}
            title="Ver ficha del activo"
        />
    );

    const tipoTemplate = (m) => (
        <Badge label={TIPO_LABELS[m.tipo] || m.tipo} colors={TIPO_COLORS[m.tipo] || TIPO_COLORS['Correctivo']} />
    );

    const costoTemplate = (m) => m.costo != null
        ? m.costo.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 })
        : <span style={{ color: '#d0d5dd' }}>—</span>;

    const fechaTemplate        = (m) => fmtFecha(m.fecha);
    const descripcionTemplate  = (m) => m.descripcion
        ? <span title={m.descripcion}>{m.descripcion.length > 50 ? m.descripcion.substring(0, 50) + '…' : m.descripcion}</span>
        : <span style={{ color: '#d0d5dd' }}>—</span>;
    const responsableTemplate  = (m) => m.responsable || <span style={{ color: '#d0d5dd' }}>—</span>;

    const accionesTemplate = (m) => (
        <div className="flex gap-2 justify-content-center">
            <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(m)} tooltip="Editar" />
            <Button icon="pi pi-trash" rounded className="btn-delete" onClick={() => handleDelete(m)} tooltip="Eliminar" />
        </div>
    );

    // ── Render ──
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#101828' }}>Mantenimiento</h4>
                    <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>
                        Programación y seguimiento de tareas de mantenimiento
                    </p>
                </div>
                <div className="flex align-items-center gap-3">
                    <Button
                        label="Nueva Tarea"
                        icon="pi pi-plus"
                        onClick={handleNuevo}
                        disabled={!barrio?.id}
                    />
                    <div className="flex" style={{ border: '1px solid #d0d5dd', borderRadius: 8, overflow: 'hidden' }}>
                        <Button
                            icon="pi pi-list"
                            label="Lista"
                            onClick={() => setVistaCalendario(false)}
                            className={vistaCalendario ? 'p-button-text' : ''}
                            style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                        />
                        <Button
                            icon="pi pi-calendar"
                            label="Calendario"
                            onClick={() => setVistaCalendario(true)}
                            className={!vistaCalendario ? 'p-button-text' : ''}
                            style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                        />
                    </div>
                </div>
            </div>

            <Dialog
                header={formData.mantenimientoId ? 'Editar Tarea de Mantenimiento' : 'Nueva Tarea de Mantenimiento'}
                visible={visible}
                style={{ width: '520px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>Ítem <span style={{ color: '#f04438' }}>*</span></label>
                            <Dropdown
                                value={formData.itemMantenimientoId}
                                options={items}
                                optionLabel="nombre"
                                optionValue="itemMantenimientoId"
                                onChange={(e) => handleChange('itemMantenimientoId', e.value)}
                                placeholder="Seleccioná un ítem"
                                className="w-full"
                                filter
                                emptyMessage="No hay ítems cargados. Creá uno en Configuración Mantenimiento."
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Fecha <span style={{ color: '#f04438' }}>*</span></label>
                            <Calendar
                                value={formData.fecha}
                                onChange={(e) => handleChange('fecha', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Tipo</label>
                            <Dropdown
                                value={formData.tipo}
                                options={TIPOS.map(t => ({ label: TIPO_LABELS[t], value: t }))}
                                onChange={(e) => handleChange('tipo', e.value)}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Descripción <span style={{ color: '#f04438' }}>*</span></label>
                            <InputTextarea
                                value={formData.descripcion}
                                onChange={(e) => handleChange('descripcion', e.target.value)}
                                rows={3}
                                autoResize
                                className="w-full"
                                placeholder="Detalle de la tarea a realizar"
                                maxLength={500}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Responsable <span style={optStyle}>(opcional)</span></label>
                            <InputText
                                value={formData.responsable}
                                onChange={(e) => handleChange('responsable', e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Nombre del técnico o empresa"
                                maxLength={200}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Costo <span style={optStyle}>(opcional)</span></label>
                            <InputNumber
                                value={formData.costo}
                                onValueChange={(e) => handleChange('costo', e.value)}
                                mode="currency"
                                currency="ARS"
                                locale="es-AR"
                                maxFractionDigits={0}
                                className="w-full"
                                placeholder="$ 0"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Estado</label>
                            <Dropdown
                                value={formData.estado}
                                options={ESTADOS.map(e => ({ label: e, value: e }))}
                                onChange={(e) => handleChange('estado', e.value)}
                                className="w-full"
                            />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label="Guardar" icon="pi pi-save" type="submit" />
                        </div>

                    </div>
                </form>
            </Dialog>

            {!barrio?.id
                ? <p style={{ color: '#98a2b3' }}>Seleccioná un barrio para ver los mantenimientos.</p>
                : vistaCalendario ? (
                    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: '0.78rem', color: '#667085', marginBottom: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            {ESTADOS.map(e => (
                                <span key={e}>
                                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: ESTADO_COLORS[e].dot, marginRight: 5 }} />
                                    {e}
                                </span>
                            ))}
                        </div>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            locale={esLocale}
                            firstDay={1}
                            headerToolbar={{
                                left:   'prev,next today',
                                center: 'title',
                                right:  'dayGridMonth,timeGridWeek',
                            }}
                            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
                            noEventsText="Sin mantenimientos"
                            events={eventosCalendario}
                            height="auto"
                            eventClick={(info) => handleEdit(info.event.extendedProps.mantenimiento)}
                            eventContent={(info) => {
                                const m = info.event.extendedProps.mantenimiento;
                                return (
                                    <div style={{ padding: '1px 4px', fontSize: '0.75rem', overflow: 'hidden', lineHeight: 1.35, cursor: 'pointer' }}>
                                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.itemNombre}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {/* Filtros fuera de la grilla */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
                                <Dropdown
                                    value={filtroItem}
                                    options={[{ itemMantenimientoId: null, nombre: 'Todos' }, ...items]}
                                    optionLabel="nombre"
                                    optionValue="itemMantenimientoId"
                                    onChange={(e) => setFiltroItem(e.value)}
                                    placeholder="Ítem"
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 160 }}
                                />
                                <Calendar
                                    value={filtroFecha}
                                    onChange={(e) => setFiltroFecha(e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Filtrar por fecha"
                                    showIcon showButtonBar
                                    className="p-inputtext-sm"
                                    style={{ width: 190 }}
                                />
                                <Dropdown
                                    value={filtroEstado}
                                    options={ESTADO_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFiltroEstado(e.value)}
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 140 }}
                                />
                                {(filtroItem || filtroFecha || filtroEstado) && (
                                    <Button
                                        icon="pi pi-filter-slash"
                                        text
                                        severity="secondary"
                                        tooltip="Limpiar filtros"
                                        onClick={() => { setFiltroItem(null); setFiltroFecha(null); setFiltroEstado(''); }}
                                    />
                                )}
                            </div>
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" style={{ left: '0.75rem' }} />
                                <InputText
                                    value={globalFilter}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Buscar…"
                                    className="p-inputtext-sm"
                                    style={{ paddingLeft: '2rem', width: 200 }}
                                />
                            </span>
                        </div>

                        <DataTable
                            value={mantenimientosFiltrados}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 30, 50]}
                            emptyMessage="No hay mantenimientos para mostrar"
                            sortField="fecha"
                            sortOrder={-1}
                            rowClassName={(m) => m.estado === 'Cancelada' ? 'opacity-50' : ''}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="itemNombre"   header="Ítem"         sortable body={itemTemplate} style={{ minWidth: 160 }} />
                            <Column field="tipo"         header="Tipo"         sortable body={tipoTemplate} style={{ minWidth: 120 }} />
                            <Column field="fecha"        header="Fecha"        sortable body={fechaTemplate} style={{ minWidth: 110 }} />
                            <Column header="Descripción" body={descripcionTemplate} style={{ minWidth: 180 }} />
                            <Column header="Responsable" body={responsableTemplate} style={{ minWidth: 140 }} />
                            <Column field="costo"        header="Costo"        sortable body={costoTemplate} style={{ minWidth: 110 }} />
                            <Column field="estado"       header="Estado"       sortable body={estadoTemplate} style={{ minWidth: 120 }} />
                            <Column header="Acciones"    body={accionesTemplate} style={{ width: '8rem' }} />
                        </DataTable>
                    </>
                )
            }

            <FichaActivoDialog
                visible={fichaVisible}
                onHide={() => setFichaVisible(false)}
                itemMantenimientoId={fichaItemId}
                administracionId={administracionId}
            />
        </>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const optStyle   = { color: '#98a2b3', fontWeight: 400 };

export default MantenimientoAdmin;
